import { makeValueFormatter, type ChartDataset } from "@/components/cinch/charts";

import type { AccuracyRow, CashFlowMonth, CashFlowWeek } from "./api";

/** Fallback cash budget when settings haven't loaded (the real value comes
 *  from cash_flow.settings via the Admin tab). */
export const DEFAULT_CASH_THRESHOLD = 50_000;

const usd = makeValueFormatter("usd");
const usdCompact = makeValueFormatter("usd_compact");

export function fmtUsd(n: number): string {
  return n < 0 ? `-${usd(Math.abs(n))}` : usd(n);
}

/** "2026-08-02" → local Date (avoids the UTC-midnight off-by-one). */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/** Short week label like the 13W model uses: "8/2" (with 'YY when asked). */
export function weekLabel(iso: string, withYear = false): string {
  const d = parseDay(iso);
  const base = `${d.getMonth() + 1}/${d.getDate()}`;
  return withYear ? `${base}/${String(d.getFullYear()).slice(2)}` : base;
}

export function monthLabel(iso: string): string {
  return parseDay(iso).toLocaleDateString("en-US", { month: "short" });
}

// ─── Waterfall (current week / month / quarter) ─────────────────────────────

export interface WaterfallStep {
  label: string;
  /** Signed delta for movement bars; the level itself for start/end bars. */
  value: number;
  kind: "start" | "delta" | "end";
}

/** A waterfall bar as stacked segments so it renders on AG Charts Community
 *  (no native waterfall series there): an invisible offset + the visible bar,
 *  once for the positive stack and once for the negative one — AG stacks
 *  positive and negative values separately, so a bar spanning [y0, y1] is
 *  emitted as whichever parts fall above and below zero. */
export interface WaterfallRow extends Record<string, string | number | null> {
  label: string;
  posOffset: number | null;
  posBar: number | null;
  negOffset: number | null;
  negBar: number | null;
  /** The number the tooltip/label shows (signed delta, or the level). */
  shown: number;
  kind: "start" | "delta" | "end";
}

export function waterfallRows(steps: WaterfallStep[]): WaterfallRow[] {
  let run = 0;
  return steps.map((s) => {
    const [y0, y1] = s.kind === "delta" ? [run, run + s.value] : [0, s.value];
    if (s.kind === "delta") run += s.value;
    else run = s.value;
    const lo = Math.min(y0, y1);
    const hi = Math.max(y0, y1);
    const posLo = Math.max(0, lo);
    const posHi = Math.max(0, hi);
    const negHi = Math.min(0, hi);
    const negLo = Math.min(0, lo);
    return {
      label: s.label,
      posOffset: posLo > 0 ? posLo : null,
      posBar: posHi - posLo > 0 ? posHi - posLo : null,
      negOffset: negHi < 0 ? negHi : null,
      negBar: negLo - negHi < 0 ? negLo - negHi : null,
      shown: s.value,
      kind: s.kind,
    };
  });
}

/** Cash on hand just before a week: its ending cash minus its net change. */
function openingCash(week: CashFlowWeek): number {
  return week.ending_cash - week.net_change;
}

/** Start → inflows → outflows → end for a single week. */
export function weekWaterfall(week: CashFlowWeek): WaterfallStep[] {
  return [
    { label: `Start\n${weekLabel(week.week_ending)}`, value: openingCash(week), kind: "start" },
    { label: "Inflows", value: week.total_inflows, kind: "delta" },
    { label: "Outflows", value: week.total_outflows, kind: "delta" },
    { label: `End\n${weekLabel(week.week_ending)}`, value: week.ending_cash, kind: "end" },
  ];
}

/** Start → one net-change bar per constituent period → end. */
export function periodWaterfall(
  weeks: CashFlowWeek[],
  label: (w: CashFlowWeek) => string,
): WaterfallStep[] {
  if (weeks.length === 0) return [];
  const first = weeks[0]!;
  const last = weeks[weeks.length - 1]!;
  return [
    { label: "Start", value: openingCash(first), kind: "start" },
    ...weeks.map((w): WaterfallStep => ({ label: label(w), value: w.net_change, kind: "delta" })),
    { label: "End", value: last.ending_cash, kind: "end" },
  ];
}

/** Merge consecutive weeks into calendar months (bucketed by week ending),
 *  summing net change — the quarter waterfall's month bars. */
export function rollupByMonth(weeks: CashFlowWeek[]): CashFlowWeek[] {
  const byMonth = new Map<string, CashFlowWeek>();
  for (const w of weeks) {
    const d = parseDay(w.week_ending);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const acc = byMonth.get(key);
    if (!acc) {
      byMonth.set(key, { ...w, week_ending: key });
    } else {
      acc.total_inflows += w.total_inflows;
      acc.total_outflows += w.total_outflows;
      acc.net_change += w.net_change;
      acc.ending_cash = w.ending_cash; // last week's level wins
    }
  }
  return [...byMonth.values()];
}

/** The week containing `today` (the first week ending on/after it). */
export function currentWeek(weeks: CashFlowWeek[], today: Date): CashFlowWeek | null {
  return weeks.find((w) => parseDay(w.week_ending) >= today) ?? null;
}

export function weeksEndingInMonth(weeks: CashFlowWeek[], today: Date): CashFlowWeek[] {
  return weeks.filter((w) => {
    const d = parseDay(w.week_ending);
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
  });
}

export function weeksEndingInQuarter(weeks: CashFlowWeek[], today: Date): CashFlowWeek[] {
  const q = Math.floor(today.getMonth() / 3);
  return weeks.filter((w) => {
    const d = parseDay(w.week_ending);
    return d.getFullYear() === today.getFullYear() && Math.floor(d.getMonth() / 3) === q;
  });
}

/** Ending cash across the model horizon (actuals + forecast). The forecast
 *  series repeats the last actual point so the two lines connect. */
export function endingCashDataset(
  weeks: CashFlowWeek[],
  cashThreshold: number = DEFAULT_CASH_THRESHOLD,
): ChartDataset {
  const model = weeks.filter((w) => w.segment !== "extension");
  const lastActual = model.reduce(
    (acc, w, i) => (w.segment === "actual" ? i : acc),
    -1,
  );
  return {
    key: "cash_flow.ending_cash",
    label: "Ending Cash by Week — Model Horizon",
    kind: "timeseries",
    chartTypes: ["line"],
    categoryKey: "week",
    rows: model.map((w, i) => ({
      week: weekLabel(w.week_ending),
      actual: w.segment === "actual" ? w.ending_cash : null,
      forecast:
        w.segment === "forecast" || i === lastActual ? w.ending_cash : null,
    })),
    series: [
      { key: "actual", name: "Actual" },
      { key: "forecast", name: "Forecast" },
    ],
    colors: [],
    valueFormat: usdCompact,
    trend: { type: "reference", mode: "value", value: cashThreshold, seriesKey: "forecast" },
  };
}

/** Forecast-vs-actual for one accuracy line item, across the three horizons.
 *
 *  Gaps are meaningful and deliberately left as nulls: a horizon only has a
 *  point for a week if a lock that far back captured one, so the lines grow as
 *  the log fills rather than implying data that was never recorded. */
export function accuracyDataset(rows: AccuracyRow[], lineItem: string): ChartDataset {
  const byWeek = new Map<string, Record<string, number | string | null>>();
  for (const row of rows) {
    if (row.line_item !== lineItem) continue;
    const point = byWeek.get(row.target_week_ending) ?? {
      week: weekLabel(row.target_week_ending),
      "1wk": null,
      "6wk": null,
      "13wk": null,
      actual: null,
    };
    point[row.horizon] = row.forecast;
    if (row.actual !== null) point.actual = row.actual;
    byWeek.set(row.target_week_ending, point);
  }
  const ordered = [...byWeek.entries()].sort(([a], [b]) => a.localeCompare(b));
  return {
    key: `cash_flow.accuracy.${lineItem}`,
    label: lineItem,
    kind: "timeseries",
    chartTypes: ["line"],
    categoryKey: "week",
    rows: ordered.map(([, point]) => point),
    series: [
      { key: "1wk", name: "Forecast 1wk" },
      { key: "6wk", name: "Forecast 6wk" },
      { key: "13wk", name: "Forecast 13wk" },
      { key: "actual", name: "Actual" },
    ],
    colors: [],
    valueFormat: usdCompact,
  };
}

/** Customer collections by month: closed actuals + the estimated tail for the
 *  current month's not-yet-closed days, stacked. */
export function collectionsDataset(months: CashFlowMonth[]): ChartDataset {
  return {
    key: "cash_flow.monthly_collections",
    label: "Customer Collections by Month",
    kind: "timeseries",
    chartTypes: ["stacked-bar"],
    categoryKey: "month",
    rows: months.map((m) => ({
      month: monthLabel(m.month),
      actual: m.actual,
      tail: m.estimated_tail || null,
    })),
    series: [
      { key: "actual", name: "Actual" },
      { key: "tail", name: "Estimated tail" },
    ],
    colors: [],
    valueFormat: usdCompact,
  };
}

/** Runway: the model forecast extended past the horizon on the same
 *  assumptions. The extension series repeats the last forecast point so the
 *  two lines connect. */
export function runwayDataset(weeks: CashFlowWeek[]): ChartDataset {
  const fcst = weeks.filter((w) => w.segment !== "actual");
  const lastForecast = fcst.reduce(
    (acc, w, i) => (w.segment === "forecast" ? i : acc),
    -1,
  );
  return {
    key: "cash_flow.runway",
    label: "Runway Extension",
    kind: "timeseries",
    chartTypes: ["line"],
    categoryKey: "week",
    rows: fcst.map((w, i) => ({
      week: weekLabel(w.week_ending, w.segment === "extension"),
      forecast:
        w.segment === "forecast" ? w.ending_cash : null,
      extension:
        w.segment === "extension" || i === lastForecast ? w.ending_cash : null,
    })),
    series: [
      { key: "forecast", name: "Model forecast" },
      { key: "extension", name: "Extension (extrapolated)" },
    ],
    colors: [],
    valueFormat: usdCompact,
    // No trend spec: the runway chart draws its own cross-lines (solid red
    // zero-cash + dashed red cash budget) in SummaryTab.
  };
}
